import { useState } from "react";
import { motion } from "framer-motion";
import { Gift, Coffee, Check, Trash2, Percent } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useLoyalty } from "@/context/LoyaltyContext";
import { useLang } from "@/context/LanguageContext";
import { getProductById } from "@/data/menu";
import { localize, formatTRY } from "@/lib/format";

// Human-readable reward from the campaign config (free product / discount / custom).
function rewardText(campaign, lang, t) {
  switch (campaign.rewardType) {
    case "free_product": {
      const product = getProductById(campaign.rewardProductId);
      return product ? localize(product.name, lang) : "";
    }
    case "discount": {
      const d = campaign.discount;
      if (!d) return "";
      return d.kind === "percent"
        ? t("rewardDiscountPct").replace("{v}", d.value)
        : t("rewardDiscountAmt").replace("{v}", formatTRY(d.value));
    }
    case "custom":
      return localize(campaign.customReward, lang);
    default:
      return "";
  }
}

const REWARD_ICON = { free_product: Coffee, discount: Percent };

export function LoyaltyCard({ compact = false }) {
  const { enabled, threshold, member, maskedPhone, join, redeem, remove, campaign } = useLoyalty();
  const { lang, t } = useLang();
  const [phone, setPhone] = useState("");

  if (!enabled) return null;
  const reward = rewardText(campaign, lang, t);
  const RewardIcon = REWARD_ICON[campaign.rewardType] || Gift;

  // Not joined -> optional join card
  if (!member) {
    if (compact) return null;
    const valid = phone.replace(/\D/g, "").length >= 10;
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl border border-border bg-card p-5 shadow-soft"
        data-testid="loyalty-join-card"
      >
        <div className="mb-2 flex items-center gap-2.5">
          <span className="grid h-10 w-10 place-items-center rounded-xl brand-gradient text-white"><Gift className="h-5 w-5" /></span>
          <h3 className="font-heading text-lg font-extrabold">{t("loyaltyJoinTitle")}</h3>
        </div>
        <p className="mb-1 text-sm font-semibold text-primary">{localize(campaign.title, lang)}</p>
        {reward && <p className="mb-3 text-xs text-muted-foreground" data-testid="loyalty-reward-text">{t("rewardLabel")} {reward}</p>}
        <p className="mb-3 text-sm text-muted-foreground">{t("loyaltyJoinDesc")}</p>
        <input
          type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
          placeholder={t("phonePlaceholder")}
          className="mb-2 h-12 w-full rounded-2xl border border-border bg-background px-4 text-[15px] outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          data-testid="loyalty-phone-input"
        />
        <p className="mb-3 text-[11px] leading-relaxed text-muted-foreground">{t("phoneConsent")}</p>
        <div className="flex gap-2">
          <Button
            onClick={() => { join(phone); setPhone(""); toast.success(t("loyaltyJoined")); }}
            disabled={!valid}
            className="h-12 flex-1 rounded-full brand-gradient font-bold text-white disabled:opacity-50"
            data-testid="loyalty-join-btn"
          >
            {t("join")}
          </Button>
        </div>
      </motion.div>
    );
  }

  const pct = Math.round((member.progress / threshold) * 100);
  const left = threshold - member.progress;
  const available = member.reward === "AVAILABLE";
  const redeemed = member.reward === "REDEEMED";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-3xl border p-4 shadow-soft ${available ? "border-primary bg-primary/5" : "border-border bg-card"}`}
      data-testid="loyalty-progress-card"
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-xl brand-gradient text-white">
            <RewardIcon className="h-5 w-5" />
          </span>
          <div>
            <p className="font-heading text-sm font-extrabold leading-tight">{localize(campaign.title, lang)}</p>
            <p className="text-[11px] text-muted-foreground">{t("loyaltyMember")} · {maskedPhone}</p>
          </div>
        </div>
      </div>

      {available ? (
        <div data-testid="loyalty-reward-available">
          <p className="mb-2 font-heading text-base font-extrabold">{t("rewardReady")}</p>
          {reward && <p className="mb-2 text-sm">{t("rewardLabel")} {reward}</p>}
          <Button onClick={() => { redeem(); toast.success(t("rewardRedeemedToast")); }} className="h-11 w-full rounded-full brand-gradient font-bold text-white" data-testid="loyalty-redeem-btn">
            {t("redeemReward")}
          </Button>
        </div>
      ) : redeemed ? (
        <>
          <p className="flex items-center gap-1.5 text-sm font-semibold text-success" data-testid="loyalty-reward-redeemed"><Check className="h-4 w-4" /> {t("rewardRedeemed")}</p>
          {member.progress > 0 && (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="loyalty-progress-after-redeem">{member.progress} / {threshold} {t("loyaltyCompleted")}</p>
          )}
        </>
      ) : (
        <>
          <div className="mb-2 flex gap-1.5" data-testid="loyalty-stamps">
            {Array.from({ length: threshold }).map((_, i) => (
              <span key={i} className={`h-2.5 flex-1 rounded-full ${i < member.progress ? "brand-gradient" : "bg-secondary"}`} />
            ))}
          </div>
          <p className="text-sm font-semibold">
            {left === 1 ? t("loyaltyAlmost") : `${t("loyaltyLeftPre")} ${left} ${t("loyaltyLeftPost")}`}
          </p>
          <p className="text-xs text-muted-foreground">{member.progress} / {threshold} {t("loyaltyCompleted")}</p>
        </>
      )}

      <button onClick={() => { remove(); toast(t("loyaltyRemoved")); }} className="mt-3 flex items-center gap-1 text-[11px] text-muted-foreground underline-offset-2 hover:underline" data-testid="loyalty-remove-btn">
        <Trash2 className="h-3 w-3" /> {t("loyaltyRemove")}
      </button>
    </motion.div>
  );
}
